import { createWidgetRegistry, type WidgetModule } from "@salestrak/ui";
// One shared definitions home: one kit, one registry, one `definitions/` folder
// — so every feature mounted under the app-level
// `<WidgetRegistryProvider registry={widgetRegistry}>` (the dashboard grid, the
// Add-Card gallery, settings surfaces) resolves EVERY widget type with no second
// registry to shadow it. The authoring catalog is curated purely by the type set
// below — the registry itself never separates resolution from authoring.
// Definitions import domain contexts via direct module paths, never a feature
// barrel — the barrels are what import this registry, so barrel imports from
// inside a definition would create cycles.
import { metricDef } from "#app/widgets/definitions/metric";
import { statStripDef } from "#app/widgets/definitions/stat-strip";
import { dimensionBarDef } from "#app/widgets/definitions/dimension-bar";
import { dimensionBatteryDef } from "#app/widgets/definitions/dimension-battery";
import { typeMixDef } from "#app/widgets/definitions/type-mix";
import { seriesLineDef } from "#app/widgets/definitions/series-line";
import { stageAgeDef } from "#app/widgets/definitions/stage-age";
import { stageFunnelDef } from "#app/widgets/definitions/stage-funnel";
import { stageConversionDef } from "#app/widgets/definitions/stage-conversion";
import { stageMovesDef } from "#app/widgets/definitions/stage-moves";
import { projectsClosedDef } from "#app/widgets/definitions/projects-closed";
import { projectsByStageDef } from "#app/widgets/definitions/projects-by-stage";
import { mostActiveUsersDef } from "#app/widgets/definitions/most-active-users";
import { entityListDef } from "#app/widgets/definitions/entity-list";
import { favoritesListDef } from "#app/widgets/definitions/favorites-list";
import { activityListDef } from "#app/widgets/definitions/activity-list";
import { teamMembersDef } from "#app/widgets/definitions/team-members";
import { portfolioMapDef } from "#app/widgets/definitions/portfolio-map";
import { stageThroughputDef } from "#app/widgets/definitions/stage-throughput";
import { noteDef } from "#app/widgets/definitions/note";
import { embedDef } from "#app/widgets/definitions/embed";
import { outcomePerformanceDef } from "#app/widgets/definitions/outcome-performance";
import { workflowRosterDef } from "#app/widgets/definitions/workflow-roster";
import { addedListDef } from "#app/widgets/definitions/added-list";
import { assignedListDef } from "#app/widgets/definitions/assigned-list";
import { followUpListDef } from "#app/widgets/definitions/follow-up-list";
import { recentListDef } from "#app/widgets/definitions/recent-list";
import { agendaDef } from "#app/widgets/definitions/agenda";
import { sectionHeaderDef } from "#app/widgets/definitions/section-header";

/**
 * THE app's single widget registry instance — created here and seeded with every
 * shipped widget as a load-time side effect of importing this module. The kit
 * itself owns no global; this is the one instance the dashboard grid, catalog,
 * and settings surfaces all read, mounted via `<WidgetRegistryProvider>` in the
 * workspace layout.
 *
 * Every widget registers its `definitions/<name>/` folder here plus its type key
 * in the server allowlist (`apps/api/core/src/widgets/widget-types.ts`), in
 * lockstep. Persisted rows whose type has no registered module render the kit's
 * `UnknownWidgetBody` placeholder.
 */
export const widgetRegistry = createWidgetRegistry({
  // A row with a seed identity (`config.widgetKey` — a dashboard template's
  // `<template>:<index>` or a managed board's key) was placed by seeding, not
  // the gallery, so it never received a preset stamp: it infers its curated
  // preset from its values and locks like a gallery placement would. A
  // user-placed row from before stamps existed has no key and stays custom.
  inferPresetFor: (_type, config) => typeof config.widgetKey === "string",
});

/**
 * THE single source of truth for the dashboard widgets: every def registers
 * from this one array, so add a def here once. Managed-only defs
 * (`favorites-list`, `team-members`, `assigned-list`, `added-list`,
 * `recent-list`, `agenda`) carry no catalog entries, so the gallery never
 * offers them; they still resolve because they are registered. `favorites-list`
 * and `added-list` are off every board and stay registered only so a stored
 * row decodes until the managed migration removes it.
 */
 
// distinct config generic; `any` erases it, exactly as the kit registry does.
const DASHBOARD_DEFS: WidgetModule<any>[] = [
  metricDef,
  statStripDef,
  dimensionBarDef,
  dimensionBatteryDef,
  typeMixDef,
  seriesLineDef,
  stageAgeDef,
  stageMovesDef,
  projectsClosedDef,
  projectsByStageDef,
  mostActiveUsersDef,
  entityListDef,
  favoritesListDef,
  activityListDef,
  teamMembersDef,
  portfolioMapDef,
  stageThroughputDef,
  stageFunnelDef,
  stageConversionDef,
  noteDef,
  embedDef,
  outcomePerformanceDef,
  workflowRosterDef,
  assignedListDef,
  followUpListDef,
  addedListDef,
  recentListDef,
  agendaDef,
  sectionHeaderDef,
];

for (const def of DASHBOARD_DEFS) widgetRegistry.register(def);

